import { supabase } from '../supabase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PayInvoice, PayInvoiceLine, PayWave } from '@shiftly/shared';
import { DateTime } from 'luxon';

interface PayInvoiceWithLines extends PayInvoice {
  lines: PayInvoiceLine[];
}

export interface UserPayData {
  invoice: PayInvoiceWithLines;
  user_pay_class: 'UK' | 'FP' | undefined;
  breakdown: {
    sales: { total: number; breakdown: any };
    scheduling: { total: number; breakdown: any };
    management: { total: number; set_totals: Record<string, { total: number }> };
    marketing: { total: number; set_totals: Record<string, { total: number }> };
    tools: { total: number; set_totals: Record<string, { total: number }> };
    'ad/ded': { total: number; set_totals: Record<string, { total: number }> };
    referrals: { total: number; set_totals: Record<string, { total: number }> };
    bonus: { total: number; breakdown: any };
  };
  totals: {
    sales_total: number;
    scheduling_total: number;
    management_total: number;
    marketing_total: number;
    tools_total: number;
    ad_ded_total: number;
    referrals_total: number;
    bonus_total: number;
    misc_total: number;
    grand_total: number;
  };
}

export const SupabasePayService = {
  async getUserPayForMonth(user_id: string, year: number, month: number): Promise<UserPayData | null> {
    const periodMonth = `${year}-${String(month).padStart(2, '0')}-01`;

    const [
      { data: invoice, error: invoiceError },
      { data: user }
    ] = await Promise.all([
      supabase
        .from('pay_invoices')
        .select(`
          *,
          lines:pay_invoice_lines(*)
        `)
        .eq('user_id', user_id)
        .eq('period_month', periodMonth)
        .maybeSingle(),
      supabase
        .from('users')
        .select('pay_class')
        .eq('id', user_id)
        .maybeSingle()
    ]);

    if (invoiceError || !invoice) return null;

    const breakdown = this.buildPayBreakdown(invoice.lines);
    const totals = this.calculateTotals(invoice, invoice.lines);

    return {
      invoice: invoice as PayInvoiceWithLines,
      user_pay_class: user?.pay_class || undefined,
      breakdown,
      totals
    };
  },

  async getUserPays(user_id: string): Promise<PayInvoiceWithLines[]> {
    const { data: invoices, error } = await supabase
      .from('pay_invoices')
      .select(`
        *,
        lines:pay_invoice_lines(*)
      `)
      .eq('user_id', user_id)
      .order('period_month', { ascending: false });

    if (error) throw error;
    return invoices as PayInvoiceWithLines[];
  },

  async getAllPaysForMonth(year: number, month: number): Promise<UserPayData[]> {
    const periodMonth = `${year}-${String(month).padStart(2, '0')}-01`;

    const [
      { data: invoices, error: invoicesError },
      { data: users, error: usersError }
    ] = await Promise.all([
      supabase
        .from('pay_invoices')
        .select(`
          *,
          lines:pay_invoice_lines(*)
        `)
        .eq('period_month', periodMonth)
        .order('last_generated_at', { ascending: false }),
      supabase
        .from('users')
        .select('id, pay_class')
    ]);

    if (invoicesError) throw invoicesError;
    if (usersError) throw usersError;

    // Create a map of user pay classes for quick lookup
    const userPayClassMap = new Map(users?.map(user => [user.id, user.pay_class]) || []);

    // Build the full pay data structure for each invoice
    return invoices?.map(invoice => {
      const breakdown = this.buildPayBreakdown(invoice.lines);
      const totals = this.calculateTotals(invoice, invoice.lines);

      return {
        invoice: invoice as PayInvoiceWithLines,
        user_pay_class: userPayClassMap.get(invoice.user_id) || undefined,
        breakdown,
        totals
      };
    }) || [];
  },

  async getPayInvoice(invoice_id: number): Promise<PayInvoiceWithLines | null> {
    const { data: invoice, error } = await supabase
      .from('pay_invoices')
      .select(`
        *,
        lines:pay_invoice_lines(*)
      `)
      .eq('id', invoice_id)
      .maybeSingle();

    if (error || !invoice) return null;
    return invoice as PayInvoiceWithLines;
  },

  async updateInvoiceStatus(invoice_id: number, status: 'generated' | 'submitted' | 'deferred'): Promise<void> {
    const { error } = await supabase
      .from('pay_invoices')
      .update({ status })
      .eq('id', invoice_id);

    if (error) throw error;
  },

  async getPayWaves(): Promise<PayWave[]> {
    const { data: waves, error } = await supabase
      .from('pay_wave')
      .select('*')
      .order('month_start', { ascending: false });

    if (error) throw error;
    return waves;
  },

  async lockPayWave(month_start: string): Promise<void> {
    const { error } = await supabase
      .from('pay_wave')
      .upsert({
        month_start,
        locked_at: DateTime.utc().toISO()
      });

    if (error) throw error;
  },

  async getInvoiceReleases(): Promise<any[]> {
    const { data: releases, error } = await supabase
      .from('invoice_releases')
      .select('*')
      .order('year', { ascending: false })
      .order('month', { ascending: false });

    if (error) throw error;
    return releases;
  },

  async updateInvoiceRelease(year: number, month: number, released: boolean): Promise<void> {
    const { error } = await supabase
      .from('invoice_releases')
      .upsert({
        year,
        month,
        released
      }, {
        onConflict: 'year,month'
      });

    if (error) throw error;
  },

  async calculateUserMonthlyPay(user_id: string, year: number, month: number): Promise<UserPayData> {
    const periodDate = DateTime.fromObject({ year, month: month, day: 1 }, { zone: 'UTC' });

    const { data, error } = await supabase.functions.invoke('pay-calculate-single-user-pay', {
      body: {
        userId: user_id,
        date: periodDate.toISO()
      }
    });

    if (error) throw error;
    return data.data;
  },

  async calculateAllUsersPay(year: number, month: number): Promise<{ enqueued_count: number; errors?: string[] }> {
    const periodDate = DateTime.fromObject({ year, month: month, day: 1 }, { zone: 'UTC' });

    const { data, error } = await supabase.functions.invoke('pay-calculate-all-users-pay', {
      body: {
        date: periodDate.toISO()
      }
    });

    if (error) throw error;
    return data.data;
  },

  async downloadInvoicePDF(user_id: string, year: number, month: number): Promise<Blob> {
    // Get the current session to include auth token
    const { data: { session } } = await supabase.auth.getSession();

    const response = await fetch(`https://xkxsqzfpnulhrsngokes.supabase.co/functions/v1/pay-download-invoice`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id,
        year,
        month
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to download invoice: ${errorText}`);
    }

    return await response.blob();
  },

  async emailInvoice(userId: string, year: number, month: number): Promise<{ success: boolean; userEmail: string; bankingDetailsLink: string; invoiceFileName: string; message: string }> {
    const { data, error } = await supabase.functions.invoke('pay-email-invoice', {
      body: {
        userId,
        year,
        month
      }
    });

    if (error) throw error;
    return data;
  },

  async bulkEmailInvoices(user_ids: string[], year: number, month: number): Promise<{ success: boolean; totalUsers: number; successCount: number; failureCount: number; results: Array<{ userId: string; userEmail: string; success: boolean; error?: string }> }> {
    const { data, error } = await supabase.functions.invoke('pay-email-invoice', {
      body: {
        user_ids,
        year,
        month
      }
    });

    if (error) throw error;
    return data;
  },

  async sendInvoiceReminderDiscordNotification(year: number, month: number): Promise<{ success: boolean; userCount: number; message: string }> {
    const periodMonth = `${year}-${String(month).padStart(2, '0')}-01`;

    const { data: invoices, error } = await supabase
      .from('pay_invoices')
      .select('user_id')
      .eq('period_month', periodMonth)
      .eq('status', 'generated');

    if (error) throw error;

    if (!invoices || invoices.length === 0) {
      return {
        success: true,
        userCount: 0,
        message: 'No invoices with "generated" status found for the specified period'
      };
    }

    const userIds = invoices.map(invoice => invoice.user_id);

    const { error: notificationError } = await supabase.functions.invoke('discord-bot-notifications', {
      body: {
        action: 'send_invoice_reminder_to_specific_users',
        user_ids: userIds
      }
    });

    if (notificationError) throw notificationError;

    return {
      success: true,
      userCount: userIds.length,
      message: `Invoice reminder notifications sent to ${userIds.length} users`
    };
  },

  buildPayBreakdown(lines: PayInvoiceLine[]) {
    const breakdown: any = {
      sales: { total: 0, breakdown: {} },
      scheduling: { total: 0, breakdown: {} },
      management: { total: 0, set_totals: {} },
      marketing: { total: 0, set_totals: {} },
      tools: { total: 0, set_totals: {} },
      'ad/ded': { total: 0, set_totals: {} },
      referrals: { total: 0, set_totals: {} },
      bonus: { total: 0, breakdown: {} }
    };

    lines.forEach(line => {
      if (line.category === 'sales') {
        const salesBreakdown = line.breakdown?.[0] || {};
        const setTotals = salesBreakdown.set_totals || {};

        // Calculate combined totals across all sets
        const combinedTotals = Object.values(setTotals).reduce((combined: any, set: any) => ({
          total: combined.total + (set.total_pay || 0),
          regular_hours: combined.regular_hours + (set.regular_hours || 0),
          weekend_ot_hours: combined.weekend_ot_hours + (set.weekend_ot_hours || 0),
          weekday_ot_hours: combined.weekday_ot_hours + (set.weekday_ot_hours || 0),
          regular_pay: combined.regular_pay + (set.regular_pay || 0),
          weekend_ot_pay: combined.weekend_ot_pay + (set.weekend_ot_pay || 0),
          weekday_ot_pay: combined.weekday_ot_pay + (set.weekday_ot_pay || 0),
          total_regular_ppv: combined.total_regular_ppv + (set.total_regular_ppv || 0),
          total_ot_ppv: combined.total_ot_ppv + (set.total_ot_ppv || 0),
          nonppv: combined.nonppv + (set.nonppv || 0)
        }), {
          total: 0,
          regular_hours: 0,
          weekend_ot_hours: 0,
          weekday_ot_hours: 0,
          regular_pay: 0,
          weekend_ot_pay: 0,
          weekday_ot_pay: 0,
          total_regular_ppv: 0,
          total_ot_ppv: 0,
          nonppv: 0
        });

        breakdown[line.category] = {
          total: line.amount_gbp,
          breakdown: {
            ...salesBreakdown,
            set_totals: setTotals,
            combined: combinedTotals
          }
        };
      } else {
        breakdown[line.category] = {
          total: line.amount_gbp,
          breakdown: line.breakdown?.[0] || {},
          set_totals: line.breakdown?.[0]?.set_totals || {}
        };
      }
    });

    return breakdown;
  },

  calculateTotals(invoice: PayInvoice, lines: PayInvoiceLine[]) {
    const totals = {
      sales_total: 0,
      scheduling_total: 0,
      management_total: 0,
      marketing_total: 0,
      tools_total: 0,
      ad_ded_total: 0,
      referrals_total: 0,
      bonus_total: 0,
      misc_total: 0,
      grand_total: invoice.amount_gbp || 0
    };

    lines.forEach(line => {
      switch (line.category) {
        case 'sales':
          totals.sales_total = line.amount_gbp;
          break;
        case 'scheduling':
          totals.scheduling_total = line.amount_gbp;
          break;
        case 'management':
          totals.management_total = line.amount_gbp;
          break;
        case 'marketing':
          totals.marketing_total = line.amount_gbp;
          break;
        case 'tools':
          totals.tools_total = line.amount_gbp;
          break;
        case 'ad/ded':
          totals.ad_ded_total = line.amount_gbp;
          break;
        case 'referrals':
          totals.referrals_total = line.amount_gbp;
          break;
        case 'bonus':
          totals.bonus_total = line.amount_gbp;
          break;
      }
    });

    totals.misc_total = totals.management_total + totals.referrals_total + totals.ad_ded_total + totals.tools_total;

    return totals;
  }
};

export const useUserPayForMonth = (user_id: string, year: number, month: number) => {
  return useQuery({
    queryKey: ['pays', user_id, year, month],
    queryFn: () => SupabasePayService.getUserPayForMonth(user_id, year, month),
    staleTime: 0,
    enabled: !!user_id
  });
};

export const useUserPays = (user_id: string) => {
  return useQuery({
    queryKey: ['pays', user_id],
    queryFn: () => SupabasePayService.getUserPays(user_id),
    enabled: !!user_id
  });
};

export const useAllPaysForMonth = (year: number, month: number) => {
  return useQuery({
    queryKey: ['pays', 'all', year, month],
    queryFn: () => SupabasePayService.getAllPaysForMonth(year, month),
    staleTime: 0
  });
};

export const usePayInvoice = (invoice_id: number) => {
  return useQuery({
    queryKey: ['pay_invoice', invoice_id],
    queryFn: () => SupabasePayService.getPayInvoice(invoice_id),
    enabled: !!invoice_id
  });
};

export const useUpdateInvoiceStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoice_id, status }: { invoice_id: number; status: 'generated' | 'submitted' | 'deferred' }) =>
      SupabasePayService.updateInvoiceStatus(invoice_id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pays'] });
      queryClient.invalidateQueries({ queryKey: ['pay_invoice'] });
    }
  });
};

export const usePayWaves = () => {
  return useQuery({
    queryKey: ['pay_waves'],
    queryFn: () => SupabasePayService.getPayWaves()
  });
};

export const useLockPayWave = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (month_start: string) => SupabasePayService.lockPayWave(month_start),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pay_waves'] });
    }
  });
};

export const useInvoiceReleases = () => {
  return useQuery({
    queryKey: ['invoice_releases'],
    queryFn: () => SupabasePayService.getInvoiceReleases()
  });
};

export const useUpdateInvoiceRelease = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ year, month, released }: { year: number; month: number; released: boolean }) =>
      SupabasePayService.updateInvoiceRelease(year, month, released),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice_releases'] });
    }
  });
};

export const useCalculateUserMonthlyPay = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ user_id, year, month }: { user_id: string; year: number; month: number }) =>
      SupabasePayService.calculateUserMonthlyPay(user_id, year, month),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pays', variables.user_id] });
      queryClient.invalidateQueries({ queryKey: ['pays', variables.user_id, variables.year, variables.month] });
      queryClient.invalidateQueries({ queryKey: ['pays', 'all', variables.year, variables.month] });
    }
  });
};

export const useCalculateAllUsersPay = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ year, month }: { year: number; month: number }) =>
      SupabasePayService.calculateAllUsersPay(year, month),
    onSuccess: () => {
      // Invalidate all pay-related queries since we're recalculating all users
      queryClient.invalidateQueries({ queryKey: ['pays'] });
    }
  });
};

export const useDownloadInvoicePDF = () => {
  return useMutation({
    mutationFn: ({ user_id, year, month }: { user_id: string; year: number; month: number }) =>
      SupabasePayService.downloadInvoicePDF(user_id, year, month),
    onSuccess: (blob, { user_id, year, month }) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Format filename
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = monthNames[month - 1];
      const yearShort = year.toString().slice(-2);

      link.download = `${user_id.slice(0, 8)}-invoice-${monthName}-${yearShort}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }
  });
};

export const useEmailInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, year, month }: { userId: string; year: number; month: number }) =>
      SupabasePayService.emailInvoice(userId, year, month),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pays', variables.userId, variables.year, variables.month] });
      queryClient.invalidateQueries({ queryKey: ['pay_invoice'] });
    }
  });
};

export const useBulkEmailInvoices = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ user_ids, year, month }: { user_ids: string[]; year: number; month: number; }) =>
      SupabasePayService.bulkEmailInvoices(user_ids, year, month),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pays'] });
      queryClient.invalidateQueries({ queryKey: ['pay_invoice'] });
    }
  });
};

export const useSendInvoiceReminderDiscordNotification = () => {
  return useMutation({
    mutationFn: ({ year, month }: { year: number; month: number }) =>
      SupabasePayService.sendInvoiceReminderDiscordNotification(year, month)
  });
};

export default SupabasePayService;